'use es6';

import { Set as ImmutableSet, fromJS } from 'immutable';
import { dispatch } from 'crm_data/flux/dispatch';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import * as SequenceV1Api from 'sequences-lib/api/SequencesImmutableV1Api';
import * as SequenceV2Api from 'sequences-lib/api/SequencesImmutableV2Api';
import { ENROLL_SUCCESS, ENROLLMENT_UPDATE_SUCCESS, FETCH_SUCCESS, PAUSE_SUCCESS, RESUME_SUCCESS, TASK_DEPENDENCY_FETCH_SUCCESS, UNENROLL_SUCCESS } from './SequenceActionTypes';
import * as Alerts from 'customer-data-ui-utilities/alerts/Alerts';
import Raven from 'raven-js';
import CommunicatorLogger from 'customer-data-tracking/loggers/CommunicatorLogger';
import * as SequenceEnrollErrorTypes from 'sequences-crm-lib/constants/SequenceEnrollErrorTypes';
import { trackSingleEnrollSuccess, trackSingleEnrollError } from 'sequences-lib/lib/CustomPageActions';
import { CRM } from 'sequences-lib/constants/Platform';
import { Link } from 'foundations-components/transitional/components/Link';
import { jsx as _jsx } from "react/jsx-runtime";
let _pendingFetchTaskDependency = ImmutableSet();
const microsoftDateTimeSettings = 'https://support.microsoft.com/en-us/help/4026213/windows-how-to-set-your-time-and-time-zone';
const appleDateTimeSettings = 'https://support.apple.com/en-us/HT203413';
export default {
  fetch(id) {
    return SequenceV1Api.fetchSequence(id).then(res => {
      dispatch(FETCH_SUCCESS, res);
      return res;
    });
  },
  handleEnrollSuccess({
    enrollment,
    objectType,
    subjectId,
    ignoreDispatch
  }) {
    if (!ignoreDispatch) {
      const dataTodispatch = fromJS(enrollment).set('objectType', objectType).set('subjectId', subjectId);
      dispatch(ENROLL_SUCCESS, dataTodispatch);
    }
    trackSingleEnrollSuccess(CRM);
  },
  enroll(...args) {
    const obj = args[0];
    const {
      sequence,
      email,
      objectType,
      subjectId
    } = obj;
    CommunicatorLogger.log('communicatorInteraction', {
      action: 'start sequence',
      activity: 'email',
      channel: 'email',
      source: 'communicator'
    });
    return SequenceV2Api.enroll(sequence, email).then(res => {
      Alerts.addSuccess('sequencesCrmLib.alerts.successMessage', {
        email: res.get('toEmail'),
        sequenceName: sequence.get('name')
      }, {
        ['data-selenium-test']: 'sequence-enrollment-alert'
      });
      this.handleEnrollSuccess({
        enrollment: res,
        sequenceName: sequence.get('name'),
        objectType,
        subjectId,
        ignoreDispatch: obj.ignoreDispatch
      });
      return res;
    }, err => {
      if (err.responseJSON && err.responseJSON.errorType === SequenceEnrollErrorTypes.SEND_TIME_TOO_FAR_IN_PAST) {
        Alerts.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'sequencesCrmLib.alerts.errorMessage',
            options: {
              email,
              sequenceName: sequence.get('name')
            }
          }),
          message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "sequencesCrmLib.alerts.tooFarInPastErrorMessage_jsx",
            options: {
              microsoftHref: microsoftDateTimeSettings,
              appleHref: appleDateTimeSettings
            },
            elements: {
              AppleSettingsLink: Link,
              MicrosoftSettingsLink: Link
            }
          }),
          type: 'danger'
        });
        Raven.captureMessage('Sequence enroll error first step in past', {
          extra: {
            statusCode: err.status,
            responseText: err.responseText
          }
        });
      } else {
        Alerts.addError('sequencesCrmLib.alerts.errorMessage', {
          email,
          sequenceName: sequence.get('name')
        });
        Raven.captureMessage('sequence enroll error', {
          extra: {
            statusCode: err.status,
            response: err.responseText
          }
        });
      }
      trackSingleEnrollError(CRM, err);
      throw err;
    });
  },
  bulkEnroll(enrollments) {
    const sequenceName = enrollments.first().get('name');
    return SequenceV2Api.bulkEnroll(enrollments).then(res => {
      const successes = res.get('successes');
      const failures = res.get('failures');
      if (failures.size === 0) {
        Alerts.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "sequencesCrmLib.bulkEnrollAlerts.successTitle"
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "sequencesCrmLib.bulkEnrollAlerts.successMessage",
            options: {
              count: successes.size,
              sequenceName
            }
          }),
          type: 'success'
        });
      } else {
        if (res.get('failuresByKey').first().get('errorType') === SequenceEnrollErrorTypes.SEND_TIME_TOO_FAR_IN_PAST) {
          Alerts.addAlert({
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'sequencesCrmLib.bulkEnrollAlerts.partialErrorTitle'
            }),
            message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "sequencesCrmLib.alerts.tooFarInPastErrorMessage_jsx",
              options: {
                microsoftHref: microsoftDateTimeSettings,
                appleHref: appleDateTimeSettings
              },
              elements: {
                AppleSettingsLink: Link,
                MicrosoftSettingsLink: Link
              }
            }),
            type: 'danger'
          });
          Raven.captureMessage('sequence bulk enroll error first step in past');
        } else {
          Alerts.addAlert({
            titleText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: 'sequencesCrmLib.bulkEnrollAlerts.partialErrorTitle'
            }),
            message: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "sequencesCrmLib.bulkEnrollAlerts.partialErrorMessage"
            }),
            type: 'warning'
          });
        }
      }
      return res;
    }, err => {
      Raven.captureMessage('sequence bulk enroll error', {
        extra: {
          statusCode: err.status,
          response: err.responseText,
          payloadSize: JSON.stringify(enrollments.toJSON()).length
        }
      });
      Alerts.addAlert({
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "sequencesCrmLib.bulkEnrollAlerts.errorTitle"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "sequencesCrmLib.bulkEnrollAlerts.errorMessage"
        }),
        type: 'danger'
      });
      throw err;
    });
  },
  updateEnrollment(enrollment, subjectId, objectType) {
    return SequenceV1Api.updateEnrollment(enrollment).then(res => {
      Alerts.addSuccess('sequencesCrmLib.alerts.updateEnrollmentMessage', {}, {
        ['data-selenium-test']: 'sequence-edit-enrollment-alert'
      });
      const dataTodispatch = fromJS(res).set('objectType', objectType).set('subjectId', subjectId);
      dispatch(ENROLLMENT_UPDATE_SUCCESS, dataTodispatch);
      return res;
    }, err => {
      Raven.captureMessage('sequence edit error', {
        extra: {
          statusCode: err.status,
          response: err.responseText
        }
      });
      Alerts.addError('sequencesCrmLib.alerts.enrollmentUpdateFailed', {});
      throw err;
    });
  },
  updateEnrollmentAlert({
    enrollment,
    subjectId,
    objectType,
    error
  }) {
    if (error) {
      Raven.captureMessage('sequence edit error', {
        extra: {
          statusCode: error.status,
          response: error.responseText
        }
      });
      Alerts.addError('sequencesCrmLib.alerts.enrollmentUpdateFailed', {});
      throw error;
    } else {
      Alerts.addSuccess('sequencesCrmLib.alerts.updateEnrollmentMessage', {}, {
        ['data-selenium-test']: 'sequence-edit-enrollment-alert'
      });
      const dataTodispatch = fromJS(enrollment).set('objectType', objectType).set('subjectId', subjectId);
      dispatch(ENROLLMENT_UPDATE_SUCCESS, dataTodispatch);
      return enrollment;
    }
  },
  pause(enrollment, subjectId, objectType) {
    const enrollmentId = enrollment.get('sequenceEnrollmentId') || enrollment.get('id');
    return SequenceV2Api.pause(enrollmentId).then(res => {
      Alerts.addSuccess('sequencesCrmLib.alerts.pauseMessage');
      const dataTodispatch = enrollment.merge({
        objectType,
        subjectId
      });
      dispatch(PAUSE_SUCCESS, dataTodispatch);
      return res;
    });
  },
  resume(enrollment, subjectId, objectType) {
    return SequenceV2Api.resume(enrollment).then(() => {
      Alerts.addSuccess('sequencesCrmLib.alerts.resumeMessage');
      dispatch(RESUME_SUCCESS, enrollment.merge({
        objectType,
        subjectId
      }));
    });
  },
  resumeAlert({
    enrollment,
    subjectId,
    objectType,
    error
  }) {
    if (error) {
      Alerts.addError('sequencesCrmLib.alerts.resumeError');
    } else {
      Alerts.addSuccess('sequencesCrmLib.alerts.resumeMessage');
      dispatch(RESUME_SUCCESS, enrollment.merge({
        objectType,
        subjectId
      }));
    }
  },
  unenroll(enrollmentId, subjectId, objectType) {
    return SequenceV1Api.unenroll(enrollmentId).then(res => {
      Alerts.addSuccess('sequencesCrmLib.alerts.unenrollMessage');
      const dataTodispatch = fromJS(res).set('objectType', objectType).set('subjectId', subjectId);
      dispatch(UNENROLL_SUCCESS, dataTodispatch);
      return res;
    }, err => {
      Raven.captureMessage('sequence unenroll error', {
        extra: {
          statusCode: err && err.status,
          response: err && err.responseText
        }
      });
      Alerts.addError('sequencesCrmLib.alerts.unenrollFailureMessage');
    });
  },
  fetchTaskDependency(taskUid) {
    if (!_pendingFetchTaskDependency.has(taskUid)) {
      _pendingFetchTaskDependency = _pendingFetchTaskDependency.add(taskUid);
      return SequenceV2Api.fetchTaskDependencies([taskUid]).then(res => dispatch(TASK_DEPENDENCY_FETCH_SUCCESS, res)).finally(() => _pendingFetchTaskDependency = _pendingFetchTaskDependency.delete(taskUid));
    }
    return Promise.resolve();
  }
};